import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { US_STATES } from '../../../constants/usStates';
import { usePoliticalOrgs } from '../../../hooks/usePoliticalOrgs';
import type { OrgLevel, PoliticalOrg } from '../../../types/PoliticalOrg';
import { ANALYTICS_EVENTS, trackEvent } from '../../../utils/analytics';

interface OrgSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaignId: string;
  action: 'support' | 'oppose';
}

const OrgSelectModal: React.FC<OrgSelectModalProps> = ({
  isOpen,
  onClose,
  campaignId,
  action,
}) => {
  const navigate = useNavigate();

  const [level, setLevel] = useState<OrgLevel>('national');
  const [selectedStateCode, setSelectedStateCode] = useState<string>('');
  const [selectedOrg, setSelectedOrg] = useState<PoliticalOrg | null>(null);

  const emptyStateSeenKeysRef = useRef<Set<string>>(new Set());

  const { orgs, loading, error } = usePoliticalOrgs({
    level,
    stateCode: selectedStateCode,
  });

  const getBaseAnalyticsProperties = () => ({
    campaign_id: campaignId,
    intent_type: action,
  });

  const getOrgAnalyticsProperties = (org: PoliticalOrg) => ({
    ...getBaseAnalyticsProperties(),
    org_id: org.id,
    org_name: org.name,
    org_level: org.level,
    state_code: org.state_code,
    party_family: org.party_family,
    donation_provider: org.donation_provider,
  });

  useEffect(() => {
    if (!isOpen) return;

    setLevel('national');
    setSelectedStateCode('');
    setSelectedOrg(null);
    emptyStateSeenKeysRef.current.clear();

    trackEvent(ANALYTICS_EVENTS.ORG_SELECT_OPENED, {
      ...getBaseAnalyticsProperties(),
      default_level: 'national',
    });

    trackEvent(ANALYTICS_EVENTS.ORG_SELECT_MODAL_SHOWN, {
      ...getBaseAnalyticsProperties(),
      default_level: 'national',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    setSelectedOrg(null);
  }, [level, selectedStateCode]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (loading) return;
    if (level !== 'state') return;
    if (!selectedStateCode) return;
    if (orgs.length > 0) return;

    const emptyStateKey = `${campaignId}:${action}:${selectedStateCode}`;

    if (emptyStateSeenKeysRef.current.has(emptyStateKey)) return;

    emptyStateSeenKeysRef.current.add(emptyStateKey);

    trackEvent(ANALYTICS_EVENTS.ORG_SELECT_EMPTY_STATE_SEEN, {
      ...getBaseAnalyticsProperties(),
      org_level: 'state',
      state_code: selectedStateCode,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, level, selectedStateCode, orgs.length, campaignId, action]);

  const handleLevelChange = (nextLevel: OrgLevel) => {
    if (nextLevel === level) return;

    setLevel(nextLevel);

    trackEvent(ANALYTICS_EVENTS.ORG_SELECT_LEVEL_CHANGED, {
      ...getBaseAnalyticsProperties(),
      previous_level: level,
      next_level: nextLevel,
      state_code: nextLevel === 'state' ? selectedStateCode || null : null,
    });
  };

  const handleStateChange = (nextStateCode: string) => {
    setSelectedStateCode(nextStateCode);

    trackEvent(ANALYTICS_EVENTS.ORG_SELECT_STATE_CHANGED, {
      ...getBaseAnalyticsProperties(),
      org_level: 'state',
      previous_state_code: selectedStateCode || null,
      next_state_code: nextStateCode || null,
    });
  };

  const handleOrgSelect = (org: PoliticalOrg) => {
    setSelectedOrg(org);

    trackEvent(ANALYTICS_EVENTS.ORG_SELECTED, getOrgAnalyticsProperties(org));
  };

  const handleConfirm = () => {
    if (!selectedOrg) {
      toast.error('Please select an organization.');
      return;
    }

    if (!campaignId || !action) {
      toast.error('Missing campaign context.');
      return;
    }

    if (!selectedOrg.is_active || !selectedOrg.is_verified || !selectedOrg.donation_url) {
      toast.error('This organization is not available for contributions yet.');
      return;
    }

    navigate(
      `/contribute?campaignId=${campaignId}&action=${action}&orgId=${selectedOrg.id}`
    );
  };

  const handleClose = () => {
    onClose();
  };

  const subtitle =
    action === 'support'
      ? 'Select where you want this support action directed.'
      : 'Select where you want this opposition action directed.';

  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex min-h-screen items-center justify-center px-4 py-6">
        <div className="fixed inset-0 bg-black bg-opacity-40" />

        <div className="relative z-50 mx-auto flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white p-6 shadow-xl">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-xl font-semibold text-gray-900">
                Choose an organization
              </Dialog.Title>
              <p className="mt-1 text-sm text-gray-600">{subtitle}</p>
            </div>

            <button
              onClick={handleClose}
              className="rounded-full px-2 py-1 text-xl leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              type="button"
              aria-label="Close organization picker"
            >
              ×
            </button>
          </div>

          <div className="mb-4 grid grid-cols-2 rounded-lg border border-gray-200 bg-gray-50 p-1">
            <button
              onClick={() => handleLevelChange('national')}
              className={`rounded-md px-4 py-2 text-sm font-medium transition ${
                level === 'national'
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              type="button"
            >
              National
            </button>

            <button
              onClick={() => handleLevelChange('state')}
              className={`rounded-md px-4 py-2 text-sm font-medium transition ${
                level === 'state'
                  ? 'bg-white text-primary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              type="button"
            >
              State
            </button>
          </div>

          {level === 'state' && (
            <div className="mb-4">
              <label
                htmlFor="state-select"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Select state
              </label>

              <select
                id="state-select"
                value={selectedStateCode}
                onChange={(event) => handleStateChange(event.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">Select a state</option>
                {US_STATES.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="org-picker-scroll min-h-0 flex-1 overflow-y-auto pr-1">
            {loading ? (
              <p className="py-8 text-center text-sm text-gray-500">
                Loading organizations...
              </p>
            ) : level === 'state' && !selectedStateCode ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
                <p className="text-sm font-medium text-gray-800">
                  Select a state to view verified state-level organizations.
                </p>
                <p className="mt-2 text-sm text-gray-600">
                  State organizations only appear when Ballotr has verified a real donation
                  or action destination.
                </p>
              </div>
            ) : orgs.length === 0 && level === 'state' && selectedStateCode ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
                <p className="text-sm font-medium text-gray-800">
                  No verified state-level organizations are available for this state yet.
                </p>
                <p className="mt-2 text-sm text-gray-600">
                  You can still choose a national organization.
                </p>
                <button
                  onClick={() => handleLevelChange('national')}
                  className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
                  type="button"
                >
                  Switch to National
                </button>
              </div>
            ) : orgs.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
                <p className="text-sm font-medium text-gray-800">
                  No verified organizations are available yet.
                </p>
                <p className="mt-2 text-sm text-gray-600">
                  Please try again later.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {orgs.map((org) => {
                  const logoSrc = org.logo_url || org.logo;
                  const isSelected = selectedOrg?.id === org.id;

                  return (
                    <button
                      key={org.id}
                      onClick={() => handleOrgSelect(org)}
                      className={`rounded-lg border p-4 text-left transition hover:shadow-md ${
                        isSelected
                          ? 'border-primary ring-2 ring-primary/30'
                          : 'border-gray-200'
                      }`}
                      type="button"
                    >
                      <div className="mb-3 flex items-center gap-3">
                        {logoSrc ? (
                          <img
                            src={logoSrc}
                            alt={org.name}
                            className="h-10 w-10 rounded-full object-contain"
                          />
                        ) : (
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-500">
                            {org.name.charAt(0)}
                          </div>
                        )}

                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900">{org.name}</p>
                          <p className="text-xs text-gray-500">
                            {org.level === 'state' ? 'State party' : 'National party'}
                          </p>
                        </div>
                      </div>

                      {org.description && (
                        <p className="text-sm leading-5 text-gray-600">
                          {org.description}
                        </p>
                      )}

                      <div className="mt-4">
                        <span
                          className={`inline-flex rounded-md px-3 py-1 text-xs font-medium ${
                            isSelected
                              ? 'bg-primary text-white'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {isSelected ? 'Selected' : 'Select'}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-5 flex items-center justify-between border-t border-gray-100 pt-4">
            <button
              onClick={handleClose}
              className="rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-800"
              type="button"
            >
              Cancel
            </button>

            <button
              onClick={handleConfirm}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!selectedOrg}
              type="button"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default OrgSelectModal;